// Custom SSO data layer (#468). Reads `GET /v1/org-sso` (masked — the sealed
// client secret never comes back, only `client_secret_set`) and drives the
// set/remove mutations. Owner-only server-side. Mirrors `lib/emailRoutes.ts`:
// round-trip over `apiFetch` reusing the dev session bearer.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { OrgRole } from './org-members';
import { SESSION_TOKEN } from './session';

/**
 * Group-claim → role map (#467): which claim carries the IdP's groups, and what
 * each group grants. It is a FLOOR, elevate-only — a mapped group can raise a
 * member to admin/owner at login, never lower anyone. So dropping a group from
 * the map does not take a role back; that is `changeMemberRole` on Members.
 */
export type OrgRoleMap = {
  readonly claim: string;
  readonly map: Readonly<Record<string, OrgRole>>;
};

/** Masked OIDC config — the secret never crosses, only the set flag. */
export type OrgSsoOidcConfig = {
  readonly issuer: string;
  readonly client_id: string;
  readonly redirect_uri: string;
  readonly claim_gate: {
    readonly required_kind: string;
    readonly required_oauth_app: string;
  };
  /** The stored map, or `null` when unset (every SSO login lands `member`). */
  readonly role_map: OrgRoleMap | null;
  readonly client_secret_set: true;
};

/** Masked SAML config — seed-provisioned; the SP private key never crosses. */
export type OrgSsoSamlConfig = {
  readonly sp_entity_id: string;
  readonly acs_url: string;
  readonly idp_entity_id: string;
  readonly idp_sso_url: string;
  readonly idp_cert: string;
  readonly sp_cert: string;
  readonly attribute_map: Readonly<Record<string, string>>;
  readonly sp_private_key_set: true;
};

/** `GET /v1/org-sso` — discriminated on `protocol` so views narrow the config. */
export type OrgSsoResponse = {
  readonly custom_sso_enabled: boolean;
  readonly enabled: boolean;
  readonly login_url: string | null;
} & (
  | { readonly protocol: 'oidc'; readonly config: OrgSsoOidcConfig }
  | { readonly protocol: 'saml'; readonly config: OrgSsoSamlConfig }
  | { readonly protocol: null; readonly config: null }
);

/** `PUT /v1/org-sso` body — OIDC only in v1; SAML rows are seed-provisioned. */
export type OrgSsoPutBody = {
  readonly protocol: 'oidc';
  readonly issuer: string;
  readonly client_id: string;
  /** Plaintext in, sealed server-side. Omit on update to keep the stored secret. */
  readonly client_secret?: string;
  readonly redirect_uri: string;
  readonly claim_gate: {
    readonly required_kind: string;
    readonly required_oauth_app: string;
  };
  /**
   * Keep-on-update, the same rule as `client_secret`: OMITTING this key carries
   * the stored map forward, an explicit `null` clears it, a value replaces it.
   * The distinction is load-bearing — a form that always sent its own idea of
   * the map would wipe an API-configured one on an unrelated edit.
   */
  readonly role_map?: OrgRoleMap | null;
};

export const ORG_SSO_QUERY_KEY = 'org_sso';

/**
 * The org's custom-SSO state. Disabled (idle) with no session, like `useMe`, and
 * behind `enabled` (default admits): the GET is OWNER-only by role
 * (`requireOwner`, `apps/api/src/routes/org-sso-admin.ts`), so a caller `/v1/me`
 * names below owner sends nothing (#7426).
 */
export function useOrgSso(
  token: string | null = SESSION_TOKEN,
  enabled: () => boolean = () => true,
) {
  return createQuery<OrgSsoResponse>(() => ({
    queryKey: [ORG_SSO_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null && enabled(),
    queryFn: async () => apiFetch<OrgSsoResponse>('/v1/org-sso', token),
  }));
}

/** Create/replace the OIDC connection. The issuer is validated live server-side. */
export async function putOrgSso(
  body: OrgSsoPutBody,
  token: string | null = SESSION_TOKEN,
): Promise<OrgSsoResponse> {
  return apiFetch<OrgSsoResponse>('/v1/org-sso', token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Remove the connection — members fall back to GitHub sign-in. Idempotent. */
export async function removeOrgSso(token: string | null = SESSION_TOKEN): Promise<OrgSsoResponse> {
  return apiFetch<OrgSsoResponse>('/v1/org-sso', token, { method: 'DELETE' });
}
